
import { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Container,
  Fade,
  Grow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material"
import {
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  CollectionsOutlined,
  Close,
  ArrowBackIos,
  ArrowForwardIos
} from "@mui/icons-material"
import { CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from "@mui/material/styles"
import axios from "axios"

// Import logos as specified in requirements
import LogoMobile from "../../assets/fav.png"
import Logo from "../../assets/amal-logo-navy.png"

const theme = createTheme({
  palette: {
    primary: {
      main: "#3D3B5C",
    },
    secondary: {
      main: "#5A5880",
    },
    background: {
      default: "#ffffff",
      paper: "#f8fafc",
    },
    text: {
      primary: "#2A2940",
      secondary: "#3D3B5C",
    },
  },
  typography: {
    fontFamily: "var(--font-sans), system-ui, sans-serif",
    h4: {
      fontWeight: 700,
      color: "#3D3B5C",
      letterSpacing: "-0.02em",
    },
    body2: {
      color: "#3D3B5C",
    },
  },
  shape: {
    borderRadius: 8,
  },
})

export default function CollectionGallery() {
  const [animatingHeart, setAnimatingHeart] = useState(null)
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    itemId: null,
  })
  const [collectionDialog, setCollectionDialog] = useState(false)
  const [confirmLockDialog, setConfirmLockDialog] = useState(false)
  const [comments, setComments] = useState({})
  
  // New state for API integration
  const [swatches, setSwatches] = useState([])
  const [collectionInfo, setCollectionInfo] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [selectedSwatchImages, setSelectedSwatchImages] = useState([])
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  
  // Loading states for individual actions
  const [loadingActions, setLoadingActions] = useState({})

  const [currentComment, setCurrentComment] = useState("")

  // New state for filtering liked swatches
  const [showOnlyLiked, setShowOnlyLiked] = useState(false)

  // State to track if collection is locked
  const [isCollectionLocked, setIsCollectionLocked] = useState(false)

  // State for token management
  const [token, setToken] = useState(null)

  // Calculate total likes and comments
  const totalLikes = swatches.filter(swatch => swatch.allow_like === 0).length
  const totalComments = swatches.filter(swatch => swatch.last_comment).length

  // Filter swatches based on showOnlyLiked state
  const filteredSwatches = showOnlyLiked 
    ? swatches.filter(swatch => swatch.allow_like === 0) 
    : swatches

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search)
  const cuc = urlParams.get("cuc")
  const cun = urlParams.get("cun")
  const coll_uc = urlParams.get("coll_uc")
  const un = urlParams.get("un") // username for authentication

  // Authenticate and get token
  const authenticate = async (username) => {
    try {
      // Check if token exists in localStorage
      const storedToken = localStorage.getItem('amal_auth_token')
      if (storedToken) {
        setToken(storedToken)
        return storedToken
      }

      // If no stored token, authenticate
      const response = await axios.post(
        'https://amal.platform.simplifii.com/api/v1/admin/authenticate',
        {
          username: username,
          password: username
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      )

      if (response.data.status === 1 && response.data.token) {
        const newToken = response.data.token
        localStorage.setItem('amal_auth_token', newToken)
        setToken(newToken)
        return newToken
      } else {
        throw new Error('Authentication failed')
      }
    } catch (error) {
      console.error("Error authenticating:", error)
      setError("Authentication failed")
      setLoading(false)
      return null
    }
  }

  // Fetch swatches from API
  const fetchSwatches = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `https://amal.platform.simplifii.com/api/v1/custom/swatches?cuc=${cuc}&cun=${cun}&coll_uc=${coll_uc}&token=${token}`
      )
      
      setSwatches(response.data.response.data || [])
      setCollectionInfo({
        client: response.data.client,
        swatches_count: response.data.swatches_count,
        collection_description: response.data.collection_description
      })

      // Check if collection is locked
      const locked = response.data.locked
      setIsCollectionLocked(locked === 1)

      setLoading(false)
    } catch (error) {
      console.error("Error fetching swatches:", error)
      setError("Failed to load collection")
      setLoading(false)
    }
  }

  // Like swatch API call
  const likeSwatch = async (swatchCode) => {
    // Set loading state for this specific action
    setLoadingActions(prev => ({ ...prev, [`like_${swatchCode}`]: true }))
    
    try {
      // Optimistic update - update UI immediately
      setSwatches(prevSwatches => 
        prevSwatches.map(swatch => 
          swatch.swatch_unique_code === swatchCode 
            ? { 
                ...swatch, 
                swatch_liked_by: "liked", // Optimistic update
                allow_like: 0,
                allow_unlike: 1
              }
            : swatch
        )
      )

      await axios.patch(
        `https://amal.platform.simplifii.com/api/v1/cards?action=Like&token=${token}`,
        { card_unique_code: swatchCode }
      )
      
      // Only refetch if the optimistic update was wrong
      // For now, we'll trust the optimistic update worked
      setSnackbar({ open: true, message: "Swatch liked successfully", severity: "success" })
    } catch (error) {
      console.error("Error liking swatch:", error)
      // Revert optimistic update on error
      setSwatches(prevSwatches => 
        prevSwatches.map(swatch => 
          swatch.swatch_unique_code === swatchCode 
            ? { 
                ...swatch, 
                swatch_liked_by: null, // Revert
                allow_like: 1,
                allow_unlike: 0
              }
            : swatch
        )
      )
      setSnackbar({ open: true, message: "Failed to like swatch", severity: "error" })
    } finally {
      // Clear loading state
      setLoadingActions(prev => ({ ...prev, [`like_${swatchCode}`]: false }))
    }
  }

  // Unlike swatch API call
  const unlikeSwatch = async (swatchCode) => {
    // Set loading state for this specific action
    setLoadingActions(prev => ({ ...prev, [`unlike_${swatchCode}`]: true }))
    
    try {
      // Optimistic update - update UI immediately
      setSwatches(prevSwatches => 
        prevSwatches.map(swatch => 
          swatch.swatch_unique_code === swatchCode 
            ? { 
                ...swatch, 
                swatch_liked_by: null, // Optimistic update
                allow_like: 1,
                allow_unlike: 0
              }
            : swatch
        )
      )

      await axios.patch(
        `https://amal.platform.simplifii.com/api/v1/cards?action=Unlike&token=${token}`,
        { card_unique_code: swatchCode }
      )
      
      setSnackbar({ open: true, message: "Swatch unliked successfully", severity: "success" })
    } catch (error) {
      console.error("Error unliking swatch:", error)
      // Revert optimistic update on error
      setSwatches(prevSwatches => 
        prevSwatches.map(swatch => 
          swatch.swatch_unique_code === swatchCode 
            ? { 
                ...swatch, 
                swatch_liked_by: "liked", // Revert
                allow_like: 0,
                allow_unlike: 1
              }
            : swatch
        )
      )
      setSnackbar({ open: true, message: "Failed to unlike swatch", severity: "error" })
    } finally {
      // Clear loading state
      setLoadingActions(prev => ({ ...prev, [`unlike_${swatchCode}`]: false }))
    }
  }

  // Comment on swatch API call
  const commentOnSwatch = async (swatchCode, comment) => {
    // Set loading state for this specific action
    setLoadingActions(prev => ({ ...prev, [`comment_${swatchCode}`]: true }))

    try {
      // Optimistic update - update UI immediately
      setSwatches(prevSwatches =>
        prevSwatches.map(swatch =>
          swatch.swatch_unique_code === swatchCode
            ? {
                ...swatch,
                last_comment: comment, // Optimistic update
                last_comment_by: "You" // Optimistic update
              }
            : swatch
        )
      )

      await axios.patch(
        `https://amal.platform.simplifii.com/api/v1/cards?action=Comment&token=${token}`,
        { card_unique_code: swatchCode, comment }
      )

      setSnackbar({ open: true, message: "Comment added successfully", severity: "success" })
    } catch (error) {
      console.error("Error commenting on swatch:", error)
      // Revert optimistic update on error
      setSwatches(prevSwatches =>
        prevSwatches.map(swatch =>
          swatch.swatch_unique_code === swatchCode
            ? {
                ...swatch,
                last_comment: null, // Revert
                last_comment_by: null // Revert
              }
            : swatch
        )
      )
      setSnackbar({ open: true, message: "Failed to add comment", severity: "error" })
    } finally {
      // Clear loading state
      setLoadingActions(prev => ({ ...prev, [`comment_${swatchCode}`]: false }))
    }
  }

  // Lock collection API call
  const lockCollection = async () => {
    setLoadingActions(prev => ({ ...prev, lockCollection: true }))

    try {
      await axios.patch(
        `https://amal.platform.simplifii.com/api/v1/cards?action=Lock&token=${token}`,
        { card_unique_code: coll_uc }
      )

      // Update the locked state
      setIsCollectionLocked(true)
      setSnackbar({ open: true, message: "Collection Submitted successfully", severity: "success" })
    } catch (error) {
      console.error("Error locking collection:", error)
      setSnackbar({ open: true, message: "Failed to lock collection", severity: "error" })
    } finally {
      setLoadingActions(prev => ({ ...prev, lockCollection: false }))
    }
  }

  useEffect(() => {
    const initializeAuth = async () => {
      if (cuc && cun && coll_uc && un) {
        // Authenticate and get token
        const authToken = await authenticate(un)
        if (authToken) {
          // Token is now set in state, fetchSwatches will be called in another useEffect
        }
      } else {
        setError("Error loading collection..")
        setLoading(false)
      }
    }

    initializeAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuc, cun, coll_uc, un])

  // Fetch swatches when token is available
  useEffect(() => {
    if (token && cuc && cun && coll_uc) {
      fetchSwatches()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const handleLike = async (swatchCode, allowLike, allowUnlike) => {
    // Check if collection is locked
    if (isCollectionLocked) {
      setSnackbar({ open: true, message: "Collection is Submitted", severity: "warning" })
      return
    }

    setAnimatingHeart(swatchCode)
    setTimeout(() => setAnimatingHeart(null), 800)

    if (allowLike === 1) {
      await likeSwatch(swatchCode)
    } else if (allowUnlike === 1) {
      await unlikeSwatch(swatchCode)
    }
  }

  const handleDoubleClick = (swatchCode, allowLike, allowUnlike) => {
    handleLike(swatchCode, allowLike, allowUnlike)
  }

  // New function to handle clicking on likes count
  const handleLikesClick = () => {
    if (totalLikes > 0) {
      setShowOnlyLiked(!showOnlyLiked)
    }
  }

  const handleViewAllImages = (swatch) => {
    // Combine primary image with first 2 additional images only (3 total max)
    const additionalImages = (swatch.additional_images || []).slice(0, 2)
    const allImages = [
      { image: swatch.primary_image },
      ...additionalImages
    ]
    setSelectedSwatchImages(allImages)
    setSelectedImageIndex(0)
    setCollectionDialog(true)
  }

  const handleComment = (swatchCode) => {
    // Check if collection is locked
    if (isCollectionLocked) {
      setSnackbar({ open: true, message: "Collection is Submitted", severity: "warning" })
      return
    }

    setCommentDialog({ open: true, itemId: swatchCode })
    setCurrentComment(comments[swatchCode] || swatches.find(swatch => swatch.swatch_unique_code === swatchCode)?.last_comment || "")
  }

  const handleSaveComment = async () => {
    // Check if collection is locked (safeguard)
    if (isCollectionLocked) {
      setSnackbar({ open: true, message: "Collection is Submitted", severity: "warning" })
      setCommentDialog({ open: false, itemId: null })
      setCurrentComment("")
      return
    }

    if (commentDialog.itemId && currentComment.trim()) {
      await commentOnSwatch(commentDialog.itemId, currentComment)
      setComments((prev) => ({
        ...prev,
        [commentDialog.itemId]: currentComment,
      }))
    }
    setCommentDialog({ open: false, itemId: null })
    setCurrentComment("")
  }

  const handleCarouselNext = () => {
    setSelectedImageIndex((prev) => (prev + 1) % selectedSwatchImages.length)
  }

  const handleCarouselPrev = () => {
    setSelectedImageIndex((prev) => (prev - 1 + selectedSwatchImages.length) % selectedSwatchImages.length)
  }

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="h6">Loading collection...</Typography>
        </Box>
      </ThemeProvider>
    )
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="h6" color="error">{error}</Typography>
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          pb: isCollectionLocked ? 10 : 14,
        }}
      >
        <Container sx={{ py: 4 }}>
          {/* Logo Section */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Box
              component="img"
              src={Logo}
              alt="Amal Logo"
              sx={{
                height: { xs: 15, sm: 30 },
                width: "auto",
                display: { xs: "none", sm: "block" }
              }}
            />
            <Box
              component="img"
              src={LogoMobile}
              alt="Amal Logo Mobile"
              sx={{
                height: 40,
                width: "auto",
                display: { xs: "block", sm: "none" }
              }}
            />
          </Box>

          {/* Collection Info Card */}
          <Card
            sx={{
              mb: 1,
              backgroundColor: "background.paper",
              boxShadow: "0 1px 3px rgba(61, 59, 92, 0.1)",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 1,
                  textAlign: "center",
                }}
              >
                Welcome, {collectionInfo.client}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: "text.secondary", 
                  mb: 1, 
                  textAlign: "center",
                  fontSize: "1rem"
                }}
              >
                {collectionInfo.collection_description}
              </Typography>

              <Box sx={{ 
                display: "flex", 
                gap: 2, 
                justifyContent: "center", 
                alignItems: "center",
                flexWrap: "wrap"
              }}>
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center", 
                  gap: 0.5,
                  minWidth: "100px"
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                    {collectionInfo.swatches_count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                    Total Swatches
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center", 
                  gap: 0.5,
                  minWidth: "100px",
                  cursor: totalLikes > 0 ? "pointer" : "default",
                  opacity: totalLikes > 0 ? 1 : 0.6,
                  transition: "opacity 0.2s ease",
                  "&:hover": totalLikes > 0 ? {
                    opacity: 0.8
                  } : {}
                }}
                  onClick={handleLikesClick}
                >
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: totalLikes > 0 ? "primary.main" : "text.secondary",
                    textDecoration: showOnlyLiked ? "underline" : "none"
                  }}>
                    {totalLikes}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                    Liked
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center", 
                  gap: 0.5,
                  minWidth: "100px"
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: totalComments > 0 ? "primary.main" : "text.secondary" }}>
                    {totalComments}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                    Comments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Locked collection indicator */}
          {isCollectionLocked && (
            <Box sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              borderRadius: 1,
              textAlign: "center",
              border: "1px solid rgba(255, 152, 0, 0.3)"
            }}>
              <Typography variant="body2" sx={{ color: "#d84315", fontWeight: 600 }}>
                🔒 This collection is Submitted. You cannot like or comment on swatches.
              </Typography>
            </Box>
          )}

          {/* Filter indicator */}
          {showOnlyLiked && (
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              backgroundColor: "rgba(90, 88, 128, 0.1)", 
              borderRadius: 1,
              textAlign: "center"
            }}>
              <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 500 }}>
                Showing {filteredSwatches.length} liked swatches
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            {filteredSwatches.map((swatch, index) => (
              <Fade in timeout={600 + index * 100} key={swatch.swatch_unique_code}>
                <Card
                  sx={{
                    width: "280px",
                    flexShrink: 0,
                    backgroundColor: "background.paper",
                    boxShadow: "0 1px 3px rgba(61, 59, 92, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(61, 59, 92, 0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, pb: 1 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "1.1rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {swatch.swatch_unique_code}
                    </Typography>
                  </CardContent>

                  <Box sx={{ position: "relative", overflow: "hidden" }}>
                    <CardMedia
                      component="img"
                      height={400}
                      image={swatch.primary_image_thumbnail.replace('w_330', 'w_400')}
                      alt={swatch.swatch_unique_code}
                      onDoubleClick={() => handleDoubleClick(swatch.swatch_unique_code, swatch.allow_like, swatch.allow_unlike)}
                      sx={{
                        cursor: "pointer",
                        transition: "transform 0.3s ease",
                        objectFit: "cover",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        onClick={() => handleViewAllImages(swatch)}
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          color: "text.secondary",
                          backdropFilter: "blur(4px)",
                          transition: "all 0.2s ease",
                          padding: "6px",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            color: "#5A5880",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <CollectionsOutlined fontSize="small" />
                      </IconButton>
                    </Box>

                    {animatingHeart === swatch.swatch_unique_code && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 2,
                          pointerEvents: "none",
                        }}
                      >
                        <Grow
                          in={true}
                          timeout={400}
                          style={{
                            transformOrigin: "center center",
                          }}
                        >
                          <Box
                            sx={{
                              animation: "heartPulse 0.8s ease-out",
                              "@keyframes heartPulse": {
                                "0%": {
                                  transform: "scale(0)",
                                  opacity: 0,
                                },
                                "15%": {
                                  transform: "scale(1.2)",
                                  opacity: 1,
                                },
                                "30%": {
                                  transform: "scale(0.95)",
                                  opacity: 1,
                                },
                                "45%": {
                                  transform: "scale(1.1)",
                                  opacity: 1,
                                },
                                "80%": {
                                  transform: "scale(1)",
                                  opacity: 1,
                                },
                                "100%": {
                                  transform: "scale(1)",
                                  opacity: 0,
                                },
                              },
                            }}
                          >
                          {swatch.allow_like === 0 ? (
                            <Favorite
                              sx={{
                                fontSize: 40,
                                color: "#5A5880",
                                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                              }}
                            />
                            ) : (
                            <></>
                          )}
                          </Box>
                        </Grow> 
                      </Box>
                    )}
                  </Box>

                  <CardActions
                    sx={{
                      px: 2.5,
                      pt: 1,
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      onClick={() => handleLike(swatch.swatch_unique_code, swatch.allow_like, swatch.allow_unlike)}
                      size="small"
                      disabled={isCollectionLocked || (swatch.allow_like === 0 && swatch.allow_unlike === 0) || loadingActions[`like_${swatch.swatch_unique_code}`]}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: swatch.allow_like === 0 ? "text.secondary" : "#5A5880",
                        transition: "all 0.2s ease",
                        padding: "6px 12px",
                        textTransform: "none",
                        minWidth: "auto",
                        "&:hover": {
                          color: "#5A5880",
                          transform: "scale(1.05)",
                        },
                        "&:disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      {loadingActions[`like_${swatch.swatch_unique_code}`] ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <>
                          {swatch.allow_like === 0 ? (
                            <Favorite sx={{ fontSize: 18 }} />
                          ) : (
                            <FavoriteBorder sx={{ fontSize: 18 }} />
                          )}
                        </>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        {swatch.allow_like === 1 ? "Like" : "Unlike"}
                      </Typography>
                    </Button>

                    <Button
                      onClick={() => handleComment(swatch.swatch_unique_code)}
                      size="small"
                      disabled={isCollectionLocked || loadingActions[`comment_${swatch.swatch_unique_code}`]}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: comments[swatch.swatch_unique_code] ? "#5A5880" : "text.secondary",
                        transition: "all 0.2s ease",
                        padding: "6px 12px",
                        textTransform: "none",
                        minWidth: "auto",
                        "&:hover": {
                          color: "#5A5880",
                          transform: "scale(1.05)",
                        },
                        "&:disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      {loadingActions[`comment_${swatch.swatch_unique_code}`] ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <ChatBubbleOutline sx={{ fontSize: 18 }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Comment
                      </Typography>
                    </Button>
                  </CardActions>

                  {/* Show last comment if exists */}
                  {swatch.last_comment && (
                    <CardContent sx={{ pt: 0, px: 2.5, pb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          p: 1.5,
                          backgroundColor: "rgba(90, 88, 128, 0.05)",
                          borderRadius: 1,
                          fontSize: "0.8rem",
                          color: "text.primary",
                        }}
                      >
                        {swatch.last_comment}
                      </Typography>
                    </CardContent>
                  )}
                </Card>
              </Fade>
            ))}
          </Box>

          {/* Copyright Section */}
          <Box sx={{
            mt: 6,
            pt: 3,
            borderTop: "1px solid rgba(61, 59, 92, 0.1)",
            textAlign: "center"
          }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                mb: 1
              }}
            >
              © 2026 Amal Embroideries Pvt
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                maxWidth: "500px",
                mx: "auto",
                lineHeight: 1.5
              }}
            >
              All images on this website are owned by Amal Embroideries Pvt and are protected by copyright law. No image may be copied, downloaded, reproduced, or used without prior written permission.
            </Typography>
          </Box>

        </Container>

        <Dialog
          open={commentDialog.open}
          onClose={() => setCommentDialog({ open: false, itemId: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Comment</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Your comment"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={currentComment}
              onChange={(e) => setCurrentComment(e.target.value)}
              placeholder="Share your thoughts about this swatch..."
              disabled={loadingActions[`comment_${commentDialog.itemId}`]}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCommentDialog({ open: false, itemId: null })}
              disabled={loadingActions[`comment_${commentDialog.itemId}`]}
            >
              Cancel
            </Button>
            {
              !currentComment.trim()
              ? (
                <></>
              )
              : (
                <Button 
                  onClick={handleSaveComment} 
                  variant="contained"
                  disabled={loadingActions[`comment_${commentDialog.itemId}`]}
                  startIcon={loadingActions[`comment_${commentDialog.itemId}`] ? <CircularProgress size={16} /> : null}
                >
                  Save Comment
                </Button>
              )
            }
          </DialogActions>
        </Dialog>

        <Dialog open={collectionDialog} onClose={() => setCollectionDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">All Images</Typography>
            <IconButton onClick={() => setCollectionDialog(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ position: "relative", textAlign: "center" }}>
              {selectedSwatchImages.length > 0 && (
                <Box
                  component="img"
                  src={selectedSwatchImages[selectedImageIndex]?.image.replace('w_330', 'w_1100')}
                  alt={`Additional image ${selectedImageIndex + 1}`}
                  sx={{
                    width: "100%",
                    height: "70vh",
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              )}
              {selectedSwatchImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handleCarouselPrev}
                    sx={{
                      position: "absolute",
                      left: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                    }}
                  >
                    <ArrowBackIos />
                  </IconButton>
                  <IconButton
                    onClick={handleCarouselNext}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                    }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    {selectedImageIndex + 1} of {selectedSwatchImages.length}
                  </Typography>
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Confirmation dialog for locking collection */}
        <Dialog
          open={confirmLockDialog}
          onClose={() => setConfirmLockDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: "primary.main" }}>
            Confirm Submission
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2, color: "text.primary" }}>
              Are you sure you want to send these swatches?
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Once submitted, this collection will be locked and you won't be able to like or comment on any swatches.
            </Alert>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setConfirmLockDialog(false)}
              sx={{
                color: "text.secondary",
                textTransform: "none"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setConfirmLockDialog(false)
                await lockCollection()
              }}
              variant="contained"
              disabled={loadingActions.lockCollection}
              startIcon={loadingActions.lockCollection ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                backgroundColor: "#3D3B5C",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#5A5880",
                }
              }}
            >
              {loadingActions.lockCollection ? "processing..." : "Yes"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnackbar}>
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Fixed Send these swatches button */}
        {!isCollectionLocked && (
          <Box sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "white",
            borderTop: "1px solid rgba(61, 59, 92, 0.1)",
            boxShadow: "0 -4px 12px rgba(61, 59, 92, 0.1)",
            padding: "16px",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
          }}>
            <Button
              variant="contained"
              size="medium"
              onClick={() => setConfirmLockDialog(true)}
              disabled={loadingActions.lockCollection}
              startIcon={loadingActions.lockCollection ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: "#3D3B5C",
                color: "white",
                padding: "12px 48px",
                fontSize: "1.25rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(61, 59, 92, 0.3)",
                transition: "all 0.3s ease",
                minWidth: "300px",
                "&:hover": {
                  backgroundColor: "#5A5880",
                  boxShadow: "0 6px 16px rgba(61, 59, 92, 0.4)",
                },
                "&:disabled": {
                  backgroundColor: "rgba(61, 59, 92, 0.5)",
                  color: "rgba(255, 255, 255, 0.7)",
                }
              }}
            >
              {loadingActions.lockCollection ? "processing..." : "Submit"}
            </Button>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  )
}
